import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { Col, Form, FormCheck, OverlayTrigger, Row, Tooltip } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import DstTabsVertical from "../../../../components/DstTabsVertical/DstTabsVertical";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { CohortIntensity } from "../../../../models/CohortIntensity";
import { CursusStageCadre } from "../../../../models/CursusStageCadre";
import { LanguageEnum } from "../../../../models/Language";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import translate from "../../../../services/Translate";
import { CursusStage, Flag } from "../../Course.d";
import { fetchFlags } from "../../Course.function";
import { ComplianceForm, CursusStageForm, EditCursusStagePopupProps, SkillBlockForm } from "./EditCursusStagePopup.d";
import {
  complianceToForm,
  cursusStageToForm,
  getChangedFields,
  isCursusStageFormValid,
  saveComplianceFields,
  updateCursusStage,
  updateCursusStageFlags,
} from "./EditCursusStagePopup.function";
import "./EditCursusStagePopup.scss";

const createComplianceForm = (type: string): ComplianceForm => ({
  certifier_name: "",
  code: "",
  end_of_study_conditions: null,
  end_of_study_defense: false,
  end_of_study_duration: null,
  end_of_study_external_superview: null,
  internship_duration_days: null,
  internship_duration_hours: null,
  internship_exemption_conditions: null,
  internship_needed: false,
  name: "",
  skill_blocks: [],
  type,
  useful_links: "[]",
});

const createEditorState = (course: CursusStage) => {
  const form = cursusStageToForm(course);
  const compliance = complianceToForm(course.external_compliance);
  const selectedFlagIds = course.flags?.map((flag) => flag.id) ?? [];

  return {
    form,
    initialForm: form,
    compliance,
    initialCompliance: compliance,
    selectedFlagIds,
    initialFlagIds: selectedFlagIds,
  };
};

const EditCursusStagePopup = ({
  course,
  language,
  onCourseUpdated,
  openPopup,
  setOpenPopup,
}: EditCursusStagePopupProps) => {
  const [activeTab, setActiveTab] = useState("general");
  const [editor, setEditor] = useState(() => createEditorState(course));
  const [showComplianceChoice, setShowComplianceChoice] = useState(false);
  const lastComplianceType = useRef(editor.compliance?.type);
  const [isSaving, setIsSaving] = useState(false);
  const [availableFlags, setAvailableFlags] = useState<Flag[]>([]);
  const [isFlagsLoading, setIsFlagsLoading] = useState(false);

  useEffect(() => {
    if (openPopup) {
      const nextEditor = createEditorState(course);
      setEditor(nextEditor);
      setShowComplianceChoice(false);
      lastComplianceType.current = nextEditor.compliance?.type;
      setActiveTab("general");
    }
  }, [course, openPopup]);

  useEffect(() => {
    if (openPopup) fetchFlags(language, setAvailableFlags, setIsFlagsLoading);
  }, [openPopup, language]);

  const _updateField = (key: keyof CursusStageForm, value: string | number | boolean | null) => {
    setEditor((previous) => ({ ...previous, form: { ...previous.form, [key]: value } }));
  };

  const _handleClose = () => {
    if (!isSaving) setOpenPopup(false);
  };

  const areDatesValid =
    editor.form.dt_start !== "" && editor.form.dt_end !== "" && editor.form.dt_end >= editor.form.dt_start;
  const flagsHaveChanged =
    JSON.stringify([...editor.selectedFlagIds].sort((a, b) => a - b)) !==
    JSON.stringify([...editor.initialFlagIds].sort((a, b) => a - b));

  const _handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (isSaving || !_isFormValid || isFlagsLoading) return;
    const payload = getChangedFields(editor.form, editor.initialForm);
    const stageHasChanged = Object.keys(payload).length > 0;
    const complianceHasChanged = JSON.stringify(editor.compliance) !== JSON.stringify(editor.initialCompliance);
    if (!stageHasChanged && !complianceHasChanged && !flagsHaveChanged) return;

    setIsSaving(true);
    try {
      if (stageHasChanged) {
        await updateCursusStage(course.id, payload);
      }
      if (complianceHasChanged) {
        await saveComplianceFields(course.id, editor.compliance);
      }
      if (flagsHaveChanged) {
        await updateCursusStageFlags(course.id, editor.selectedFlagIds);
      }
      setEditor((previous) => ({
        ...previous,
        initialForm: previous.form,
        initialCompliance: previous.compliance,
        initialFlagIds: [...previous.selectedFlagIds],
      }));
      onCourseUpdated();
      setOpenPopup(false);
    } catch (error) {
      console.error("Error saving cursus stage:", error);
      alert(translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.SAVE_ERROR"));
    } finally {
      setIsSaving(false);
    }
  };

  const _isFormValid = isCursusStageFormValid(editor.form, areDatesValid, editor.compliance);

  const _hasChanges = useMemo(
    () =>
      Object.keys(getChangedFields(editor.form, editor.initialForm)).length > 0 ||
      JSON.stringify(editor.compliance) !== JSON.stringify(editor.initialCompliance) ||
      flagsHaveChanged,
    [editor.form, editor.initialForm, editor.compliance, editor.initialCompliance, flagsHaveChanged]
  );

  const textArea = (key: keyof CursusStageForm, rows = 3) => (
    <Col md={6}>
      <Form.Label>{translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.${key.toUpperCase()}`)}</Form.Label>
      <Form.Control
        as="textarea"
        rows={rows}
        value={editor.form[key] as string}
        onChange={(event) => _updateField(key, event.target.value)}
      />
    </Col>
  );

  const _tabTitle = (key: string) => translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.TABS.${key}`);
  const _sectionTitle = (key: string) => <h3 className="mb-4">{_tabTitle(key)}</h3>;

  const _updateComplianceField = (key: string, value: string | number | boolean | null | SkillBlockForm[]) => {
    if (!editor.compliance) return;
    setEditor((previous) => ({
      ...previous,
      compliance: previous.compliance ? { ...previous.compliance, [key]: value } : null,
    }));
  };

  const _offerComplianceChoice = (nextType: string) => {
    if (nextType !== lastComplianceType.current) {
      lastComplianceType.current = nextType;
      setShowComplianceChoice(true);
    }
  };

  const _resetCompliance = () => {
    if (isSaving || !editor.compliance) return;
    setEditor((previous) => ({
      ...previous,
      compliance: {
        type: editor.compliance!.type,
        certifier_name: editor.compliance!.certifier_name,
        name: "",
        code: "",
        internship_needed: false,
        internship_duration_hours: null,
        internship_duration_days: null,
        internship_exemption_conditions: null,
        end_of_study_defense: false,
        end_of_study_duration: null,
        end_of_study_conditions: null,
        end_of_study_external_superview: null,
        useful_links: "[]",
        skill_blocks: [],
      },
    }));
    setShowComplianceChoice(false);
  };

  const _updateSkillBlock = (index: number, key: string, value: string | boolean) => {
    if (!editor.compliance) return;
    _updateComplianceField(
      "skill_blocks",
      editor.compliance.skill_blocks.map((block, position) => (position === index ? { ...block, [key]: value } : block))
    );
  };

  const _handleSkillBlockDragEnd = (result: DropResult) => {
    if (!editor.compliance || !result.destination) return;
    const blocks = [...editor.compliance.skill_blocks];
    const [movedBlock] = blocks.splice(result.source.index, 1);
    blocks.splice(result.destination.index, 0, movedBlock);
    _updateComplianceField(
      "skill_blocks",
      blocks.map((block, order) => ({ ...block, order }))
    );
  };

  const overallInformationTab = (
    <div className="bg-body p-4 h-100 s-regular">
      {_sectionTitle("OVERALL_INFORMATION")}
      <Row className="g-3">
        <Col md={6}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DEPLOYMENT_NAME")} />
          </Form.Label>
          <Form.Control
            value={editor.form.deployment_name}
            onChange={(event) => _updateField("deployment_name", event.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.CURSUS_PREFIX")} />
          </Form.Label>
          <Form.Control
            value={editor.form.cursus_prefix}
            onChange={(event) => _updateField("cursus_prefix", event.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DT_START")} />
          </Form.Label>
          <Form.Control
            type="date"
            value={editor.form.dt_start}
            onChange={(event) => _updateField("dt_start", event.target.value)}
            required
            isInvalid={!areDatesValid}
          />
        </Col>
        <Col md={6}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DT_END")} />
          </Form.Label>
          <Form.Control
            type="date"
            value={editor.form.dt_end}
            min={editor.form.dt_start}
            onChange={(event) => _updateField("dt_end", event.target.value)}
            required
            isInvalid={!areDatesValid}
          />
        </Col>
        <Col md={4}>
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
          </Form.Label>
          <Form.Select
            value={editor.form.language}
            onChange={(event) => _updateField("language", event.target.value)}
            required
          >
            {Object.values(LanguageEnum).map((value) => (
              <option key={value} value={value}>
                {translate(
                  language,
                  `PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.${value === LanguageEnum.FR ? "LANGUAGE_FR" : "LANGUAGE_EN"}`
                )}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DEFAULT_CADRE")} />
          </Form.Label>
          <Form.Select
            value={editor.form.default_cadre}
            onChange={(event) => _updateField("default_cadre", event.target.value)}
            required
          >
            {Object.values(CursusStageCadre).map((value) => (
              <option key={value} value={value}>
                {value === CursusStageCadre.DEFAULT
                  ? translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DEFAULT_CADRE_LIORA")
                  : value.toUpperCase()}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>
            {translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.PRIORITY")}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="cursus-stage-priority-tooltip">
                  {translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.PRIORITY_HELP")}
                </Tooltip>
              }
            >
              <span className="dst-icon-info-circle ms-1 text-muted clickable" tabIndex={0} />
            </OverlayTrigger>
          </Form.Label>
          <Form.Control
            type="number"
            min="0"
            value={editor.form.priority ?? ""}
            onChange={(event) =>
              _updateField("priority", event.target.value === "" ? null : Number(event.target.value))
            }
          />
        </Col>
        <Col md={8} className="d-flex align-items-end pb-2">
          <Form.Check className="d-flex align-items-center gap-2 ps-0" type="switch">
            <Form.Check.Input
              className="m-0 flex-shrink-0"
              id="cursus-stage-disabled"
              checked={editor.form.is_disabled}
              onChange={(event) => _updateField("is_disabled", event.target.checked)}
            />
            <Form.Check.Label htmlFor="cursus-stage-disabled">
              {translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.IS_DISABLED")}
            </Form.Check.Label>
          </Form.Check>
        </Col>
      </Row>
    </div>
  );

  const formatTab = (
    <div className="bg-body p-4 h-100 s-regular">
      {_sectionTitle("FORMAT")}
      <Row className="g-3">
        <Col md={12}>
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.FORMAT")} />
          </Form.Label>
          <Form.Select
            value={editor.form.format}
            onChange={(event) => _updateField("format", event.target.value)}
            required
          >
            {Object.values(CohortIntensity).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Form.Select>
        </Col>
        {["total_hours", "hours_spent_on_project", "hours_spent_on_modules", "hours_spent_on_masterclass"].map(
          (key) => (
            <Col md={6} key={key}>
              <Form.Label>
                {translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.${key.toUpperCase()}`)}
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={(editor.form[key as keyof CursusStageForm] as number | null) ?? ""}
                onChange={(event) =>
                  _updateField(
                    key as keyof CursusStageForm,
                    event.target.value === "" ? null : Number(event.target.value)
                  )
                }
              />
            </Col>
          )
        )}
      </Row>
    </div>
  );

  const contractualizationTab = (
    <div className="bg-body p-4 h-100 s-regular">
      {_sectionTitle("CONTRACTUALIZATION")}
      <Row className="g-3">
        {textArea("objectives")}
        {textArea("prerequisites")}
        {textArea("content")}
        {textArea("training_organization")}
        {textArea("evaluations")}
        {textArea("end_of_formation_document")}
        {textArea("mediator")}
        {textArea("pedagogy_leader_references")}
        {textArea("pedagogy_leader_diplomas")}
        <Col md={12}>
          <Form.Label>
            {translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.PRESENTATION_DOCUMENT_URL")}
          </Form.Label>
          <Form.Control
            type="url"
            value={editor.form.presentation_document_url}
            onChange={(event) => _updateField("presentation_document_url", event.target.value)}
          />
        </Col>
      </Row>
    </div>
  );

  const flagsTab = (
    <div className="bg-body p-4 h-100 xs-regular">
      {_sectionTitle("FLAGS")}

      <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/cursus/stages/*/flags" }]}>
        <div className="py-3">
          {isFlagsLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <span className="spinner-border spinner-border-sm" />
            </div>
          ) : (
            availableFlags.map((flag) => (
              <FormCheck
                key={flag.id}
                id={`stage-flag-${flag.id}`}
                type="checkbox"
                inline
                label={flag.name}
                checked={editor.selectedFlagIds.includes(flag.id)}
                onChange={(event) => {
                  setEditor((previous) => ({
                    ...previous,
                    selectedFlagIds: event.target.checked
                      ? [...previous.selectedFlagIds, flag.id]
                      : previous.selectedFlagIds.filter((id) => id !== flag.id),
                  }));
                }}
              />
            ))
          )}
        </div>
      </PermissionComponent>
    </div>
  );

  const certifierTab = editor.compliance ? (
    <div className="bg-body p-4 s-regular">
      {_sectionTitle("CERTIFIER")}
      {showComplianceChoice && (
        <div className="alert alert-warning border-4 p-3 d-flex flex-wrap align-items-center gap-3 mb-4" role="status">
          <span className="me-auto fw-semibold">
            {translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.CHANGE_CHOICE")}
          </span>
          <DstButton
            type="button"
            variant="primary"
            btnWidth="auto"
            value={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.KEEP")}
            clickFunction={() => setShowComplianceChoice(false)}
          />
          <DstButton
            type="button"
            variant="outline-primary"
            btnClass="s-regular"
            btnWidth="auto"
            value={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.RESET")}
            clickFunction={_resetCompliance}
          />
        </div>
      )}
      <Row className="g-3">
        <Col md={6}>
          <Form.Group controlId="compliance-type">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.TYPE")} />
            </Form.Label>
            <Form.Select
              value={editor.compliance.type}
              required
              onChange={(event) => {
                if (!event.target.value) {
                  setEditor((previous) => ({ ...previous, compliance: null }));
                  setShowComplianceChoice(false);
                  return;
                }
                _updateComplianceField("type", event.target.value);
                _offerComplianceChoice(event.target.value);
              }}
            >
              <option value="">{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.NONE")}</option>
              {["rncp", "rs", "azav"].map((type) => (
                <option key={type} value={type}>
                  {type.toUpperCase()}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        {(["name", "code", "certifier_name"] as const).map((key) => (
          <Col md={6} key={key}>
            <Form.Group controlId={`compliance-${key}`}>
              <Form.Label>
                <RequiredLabel
                  label={translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.${key.toUpperCase()}`)}
                />
              </Form.Label>
              <Form.Control
                value={editor.compliance![key]}
                maxLength={255}
                required
                onChange={(event) => _updateComplianceField(key, event.target.value)}
              />
            </Form.Group>
          </Col>
        ))}
        {(["internship_needed", "end_of_study_defense"] as const).map((key) => (
          <Col md={6} key={key}>
            <Form.Check className="d-flex align-items-center gap-2 ps-0" type="switch">
              <Form.Check.Input
                className="m-0 flex-shrink-0"
                id={`compliance-${key}`}
                checked={editor.compliance![key]}
                onChange={(event) => _updateComplianceField(key, event.target.checked)}
              />
              <Form.Check.Label htmlFor={`compliance-${key}`}>
                {translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.${key.toUpperCase()}`)}
              </Form.Check.Label>
            </Form.Check>
          </Col>
        ))}
        {(["internship_duration_hours", "internship_duration_days", "end_of_study_duration"] as const).map((key) => (
          <Col md={6} key={key}>
            <Form.Group controlId={`compliance-${key}`}>
              <Form.Label>
                {translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.${key.toUpperCase()}`)}
              </Form.Label>
              <Form.Control
                type="number"
                min={0}
                step={1}
                value={editor.compliance![key] ?? ""}
                onChange={(event) =>
                  _updateComplianceField(key, event.target.value === "" ? null : Number(event.target.value))
                }
              />
            </Form.Group>
          </Col>
        ))}
        <Col md={6}>
          <Form.Group controlId="compliance-supervision">
            <Form.Label>
              {translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.END_OF_STUDY_EXTERNAL_SUPERVIEW")}
            </Form.Label>
            <Form.Select
              value={
                editor.compliance.end_of_study_external_superview === null
                  ? ""
                  : String(editor.compliance.end_of_study_external_superview)
              }
              onChange={(event) =>
                _updateComplianceField(
                  "end_of_study_external_superview",
                  event.target.value === "" ? null : event.target.value === "true"
                )
              }
            >
              <option value="">{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.UNSPECIFIED")}</option>
              <option value="true">{translate(language, "GLOBAL.YES")}</option>
              <option value="false">{translate(language, "GLOBAL.NO")}</option>
            </Form.Select>
          </Form.Group>
        </Col>
        {(["internship_exemption_conditions", "end_of_study_conditions"] as const).map((key) => (
          <Col md={12} key={key}>
            <Form.Group controlId={`compliance-${key}`}>
              <Form.Label>
                {translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.${key.toUpperCase()}`)}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editor.compliance![key] ?? ""}
                onChange={(event) => _updateComplianceField(key, event.target.value || null)}
              />
            </Form.Group>
          </Col>
        ))}
        <Col md={12}>
          <Form.Group controlId="compliance-links">
            <Form.Label>{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.USEFUL_LINKS")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={(() => {
                try {
                  const links: unknown = JSON.parse(editor.compliance.useful_links);
                  return Array.isArray(links) ? links.join("\n") : editor.compliance.useful_links;
                } catch {
                  return editor.compliance.useful_links;
                }
              })()}
              onChange={(event) =>
                _updateComplianceField("useful_links", JSON.stringify(event.target.value.split("\n")))
              }
            />
            <Form.Text>{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.LINKS_HELP")}</Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <h4 className="mt-4 mb-3">{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.SKILL_BLOCKS")}</h4>
      {editor.compliance.skill_blocks.length === 0 && (
        <div className="border rounded p-3 text-center text-muted">
          {translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.NO_BLOCKS")}
        </div>
      )}
      <DragDropContext onDragEnd={_handleSkillBlockDragEnd}>
        <Droppable droppableId="skill-blocks">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {editor.compliance!.skill_blocks.map((block, index) => (
                <Draggable
                  key={block.id ? `skill-block-${block.id}` : `new-skill-block-${index}`}
                  draggableId={block.id ? `skill-block-${block.id}` : `new-skill-block-${index}`}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="border rounded bg-body p-2 mb-2"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          {...provided.dragHandleProps}
                          className="dst-icon-align-justify d-flex align-items-center align-self-stretch bg-primary-subtle text-primary border rounded px-2"
                        />
                        <Form.Group className="flex-grow-1" controlId={`block-${index}-name`}>
                          <Form.Label>
                            <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
                          </Form.Label>
                          <Form.Control
                            size="sm"
                            value={block.name}
                            required
                            maxLength={255}
                            onChange={(event) => _updateSkillBlock(index, "name", event.target.value)}
                          />
                        </Form.Group>
                        <div className="d-flex align-items-center justify-content-center text-nowrap bg-body-tertiary border rounded px-2 py-1 ms-2">
                          <Form.Check className="d-flex align-items-center gap-2 ps-0 mb-0">
                            <Form.Check.Input
                              className="m-0"
                              id={`block-${index}-mandatory`}
                              checked={block.is_mandatory}
                              onChange={(event) => _updateSkillBlock(index, "is_mandatory", event.target.checked)}
                            />
                            <Form.Check.Label className="mb-0" htmlFor={`block-${index}-mandatory`}>
                              {translate(language, "GLOBAL.MANDATORY")}
                            </Form.Check.Label>
                          </Form.Check>
                        </div>
                        <div className="border-start ms-2 ps-3">
                          <DstButton
                            type="button"
                            variant="danger"
                            btnClass="px-2 py-1"
                            btnImageBefore={<span className="dst-icon-trash-03" />}
                            value=""
                            tooltip={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.REMOVE")}
                            clickFunction={() =>
                              _updateComplianceField(
                                "skill_blocks",
                                editor
                                  .compliance!.skill_blocks.filter((_, position) => position !== index)
                                  .map((item, order) => ({ ...item, order }))
                              )
                            }
                          />
                        </div>
                      </div>
                      <Form.Control
                        className="mt-2"
                        size="sm"
                        as="textarea"
                        rows={2}
                        value={block.content}
                        placeholder={translate(language, "GLOBAL.CONTENT")}
                        onChange={(event) => _updateSkillBlock(index, "content", event.target.value)}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="mt-3">
        <DstButton
          type="button"
          variant="outline-primary"
          btnSize="sm"
          btnWidth="auto"
          btnImageBefore={<span className="dst-icon-plus me-2" />}
          value={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.ADD")}
          clickFunction={() =>
            _updateComplianceField("skill_blocks", [
              ...editor.compliance!.skill_blocks,
              { name: "", content: "", is_mandatory: false, order: editor.compliance!.skill_blocks.length },
            ])
          }
        />
      </div>
    </div>
  ) : (
    <div className="bg-body p-4 s-regular">
      <h3 className="mb-4">{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.TITLE")}</h3>
      <Row className="g-3">
        <Col md={6}>
          <Form.Group controlId="compliance-type">
            <Form.Label>{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.TYPE")}</Form.Label>
            <Form.Select
              value=""
              onChange={(event) => {
                if (event.target.value) {
                  lastComplianceType.current = event.target.value;
                  setEditor((previous) => ({
                    ...previous,
                    compliance: editor.initialCompliance
                      ? { ...editor.initialCompliance, type: event.target.value }
                      : createComplianceForm(event.target.value),
                  }));
                }
              }}
            >
              <option value="">{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.NONE")}</option>
              {["rncp", "rs", "azav"].map((type) => (
                <option key={type} value={type}>
                  {type.toUpperCase()}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <p className="text-muted mt-3 mb-0">{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.EMPTY")}</p>
    </div>
  );

  return (
    <React.Fragment>
      <Popup
        show={openPopup}
        onClose={_handleClose}
        modalTitle={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.TITLE")}
        size="xl"
        scrollable
        removePadding
        footerContent={
          <React.Fragment>
            <DstButton
              variant="light"
              value={translate(language, "GLOBAL.CANCEL")}
              clickFunction={_handleClose}
              disabled={isSaving}
            />
            <DstButton
              type="submit"
              variant="primary"
              value={translate(language, "GLOBAL.SAVE")}
              clickFunction={_handleSubmit}
              disabled={isSaving || isFlagsLoading || !_isFormValid || !_hasChanges}
              loading={isSaving}
            />
          </React.Fragment>
        }
      >
        <Form id="edit-cursus-stage-form" onSubmit={_handleSubmit}>
          <fieldset disabled={isSaving}>
            <DstTabsVertical
              activeKey={activeTab}
              bodyClass="medium-gray-background p-3"
              classes="pt-0 edit-cursus-stage-tabs"
              onSelect={(key) => key && setActiveTab(key)}
              titleIcon={<React.Fragment></React.Fragment>}
              variant="light"
              TabsSize={{ xs: 12, sm: 4, md: 3, lg: 3, xl: 3 }}
              tabsData={[
                {
                  id: "general",
                  title: _tabTitle("OVERALL_INFORMATION"),
                  content: overallInformationTab,
                },
                {
                  id: "format",
                  title: _tabTitle("FORMAT"),
                  content: formatTab,
                },
                {
                  id: "contractualization",
                  title: _tabTitle("CONTRACTUALIZATION"),
                  content: contractualizationTab,
                },
                {
                  id: "certifier",
                  title: _tabTitle("CERTIFIER"),
                  content: certifierTab,
                },
                {
                  id: "flags",
                  title: _tabTitle("FLAGS"),
                  content: flagsTab,
                },
              ]}
            />
          </fieldset>
        </Form>
      </Popup>
    </React.Fragment>
  );
};
export default EditCursusStagePopup;
